<template>
  <v-container fluid class="px-0">
    <v-row>
      <v-col cols="12" md="5">
        <v-card outlined class="fill-height">
          <v-card-title class="subtitle-1 pb-0">{{ $t('basicInformation') }}</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="8">
                <LoadableComponent :loading="loading">
                  <v-text-field
                    v-model="tract.name"
                    id="tract-name"
                    ref="firstField"
                    :label="$t('tractName')"
                    :rules="[rules.required]"
                    color="black"
                    counter
                    maxlength="40"
                    persistent-hint
                    :hint="$t('required')"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" md="4">
                <LoadableComponent :loading="loading">
                  <v-text-field
                    v-model="tract.code"
                    id="tract-code"
                    :label="$t('tractCode')"
                    color="black"
                    counter
                    maxlength="10"/>
                </LoadableComponent>
              </v-col>
              <!-- TODO: unhide tract group selection for bid promotion once groups are applicable to promoted (delivered) tracts -->
              <v-col cols="12" class="mt-3" v-if="!tractTypeFilter">
                <LoadableComponent :loading="loading">
                  <TractGroupAutocomplete
                    :tractGroupId="tract.tractGroupId"
                    @tract-group-chosen="tractGroupChosen"
                    :fetchOnCreation="false"
                    :readonly="editing && !!propTract.tractGroupId"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" class="mt-3">
                <LoadableComponent :loading="loading">
                  <TractTypeSelector
                    id="tract-type"
                    :tractTypeId="tract.type.tractTypeId"
                    @tract-type="tractTypeChosen"
                    :hint="$t('required')"
                    :persistentHint="true"
                    :propFetchTractTypes="false"
                    :readonly="!!selectedTractGroup"
                    :filter="tractTypeFilter"
                    :disableItem="item => item.specialization === TractTypeSpecialization.ObligationCapped.value || item.specialization === TractTypeSpecialization.ObligationUncapped.value"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="6" class="mt-3">
                <LoadableComponent :loading="loading">
                  <v-autocomplete
                    clearable
                    :items="allHarvestTypes"
                    itemText="name"
                    itemValue="harvestTypeId"
                    :label="$t('harvestType')"
                    v-model="tract.harvestTypeId"
                    v-bind="requirementAttrs(TractRequiredField.HarvestType)"
                    id="harvest-type-autocomplete"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="6" class="mt-3">
                <LoadableComponent :loading="loading">
                  <v-autocomplete
                    clearable
                    :items="allLoggingMethods"
                    itemText="name"
                    itemValue="loggingMethodId"
                    :label="$t('loggingMethod')"
                    v-model="tract.loggingMethodId"
                    v-bind="requirementAttrs(TractRequiredField.LoggingMethod)"
                    id="logging-method-autocomplete"/>
                </LoadableComponent>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="7">
        <v-card outlined class="inherited-properties-card mb-5" v-if="selectedTractGroup">
          <v-card-title class="subtitle-1 pb-0">
            {{ $t('inheritedFromTractGroup') }}
            <v-chip small class="ml-2" color="info" text-color="white">{{ selectedTractGroup.name }}</v-chip>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-row no-gutters v-for="(prop) in inheritedProperties.slice(0, Math.ceil(inheritedProperties.length / 2))" :key="prop.key">
                  <v-col cols="5">{{ $t(prop.key) }}:</v-col>
                  <v-col cols="7">
                    <span :style="{ color: prop.value === null ? 'rgba(0,0,0,0)' : undefined }">
                      {{ prop.value || $t('notSpecified') }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" md="6">
                <v-row no-gutters v-for="(prop) in inheritedProperties.slice(Math.ceil(inheritedProperties.length / 2))" :key="prop.key">
                  <v-col cols="5">{{ $t(prop.key) }}:</v-col>
                  <v-col cols="7">
                    <span :style="{ color: prop.value === null ? 'rgba(0,0,0,0.4)' : undefined }">
                      {{ prop.value || $t('notSpecified') }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
        <v-card outlined>
          <v-card-title class="subtitle-1 pb-0">{{ $t('accounts') }}</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" md="6">
                <LoadableComponent :loading="loading">
                  <AccountAutocomplete
                    id="supplier"
                    :accountId="tract.supplierAccountId"
                    :title="$t('supplier')"
                    clearable
                    showCertified
                    userSetting="supplierAccountAutocomplete"
                    :hint="requirement(TractRequiredField.SupplierAccount).hint"
                    :required="requirement(TractRequiredField.SupplierAccount).required"
                    @account-chosen="supplierChosen"
                    :fetchTagsAndAccounts="false"
                    hide-refresh
                    :tabFilter="false"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" md="6">
                <LoadableComponent :loading="loading">
                  <AccountAutocomplete
                    id="consulting-forester"
                    :accountId="tract.consultingForesterAccountId"
                    :title="$t('consultingForester')"
                    clearable
                    showCertified
                    userSetting="consultingForesterAccountAutocomplete"
                    :hint="requirement(TractRequiredField.ConsultingForesterAccount).hint"
                    :required="requirement(TractRequiredField.ConsultingForesterAccount).required"
                    @account-chosen="consultingForesterChosen"
                    :fetchTagsAndAccounts="false"
                    :readonly="!!selectedTractGroup"
                    hide-refresh
                    :tabFilter="false"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" md="6" class="mt-n3">
                <LoadableComponent :loading="loading">
                  <AccountAutocomplete
                    id="tract-logger"
                    :accountId="tract.loggerAccountId"
                    :title="$t('defaultLogger')"
                    clearable
                    showCertified
                    userSetting="defaultLoggerAutocomplete"
                    :hint="requirement(TractRequiredField.LoggerAccount).hint"
                    :required="requirement(TractRequiredField.LoggerAccount).required"
                    @account-chosen="defaultLoggerChosen"
                    :fetchTagsAndAccounts="false"
                    hide-refresh
                    :tabFilter="false"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" md="6" class="mt-n3">
                <LoadableComponent :loading="loading">
                  <AccountAutocomplete
                    id="hauler"
                    :accountId="tract.haulerAccountId"
                    :title="$t('hauler')"
                    clearable
                    showCertified
                    userSetting="haulerAccountAutocomplete"
                    :hint="requirement(TractRequiredField.HaulerAccount).hint"
                    :required="requirement(TractRequiredField.HaulerAccount).required"
                    @account-chosen="haulerChosen"
                    :fetchTagsAndAccounts="false"
                    hide-refresh
                    :tabFilter="false"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" md="6" class="mt-n3">
                <LoadableComponent :loading="loading">
                  <AccountAutocomplete
                    id="contract-account"
                    :accountId="tract.contractAccountId"
                    :title="$t('contractAccount')"
                    clearable
                    showCertified
                    userSetting="tractContractAccountAutocomplete"
                    @account-chosen="contractAccountChosen"
                    :fetchTagsAndAccounts="false"
                    :ticketBookAccounts="tractTicketBookAccounts"
                    hide-refresh
                    :tabFilter="false"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" md="6" class="mt-n3">
                <LoadableComponent :loading="loading">
                  <SettingAutocomplete
                    :settingId="tract.contractSettingId"
                    @setting-chosen="contractSettingChosen"
                    hideRefresh
                    elementId="tract-contract-setting"
                    :propFetchSettings="false"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" v-if="!isGrouped">
                <v-row>
                  <v-col cols="6" v-if="isStumpageTract">
                    <LoadableComponent :loading="loading">
                      <EntitySelector
                        :entities="entities"
                        :initialEntity.sync="tract.businessEntity"
                        @entity-selected="entitySelected"
                        :hint="$t('required')"
                        :persistentHint="true"
                        :label="$t('purchaser')"
                        elementId="purchaser"
                        :disabled="!!selectedTractGroup"/>
                    </LoadableComponent>
                  </v-col>
                  <v-col cols="6">
                    <LoadableComponent :loading="loading">
                      <MoneyTextField v-if="type?.specialization !== TractTypeSpecialization.Depletion.value && !isObligationUncapped"
                        elementId="tract-cost"
                        :initialValue="tract.cost"
                        :label="isObligationCapped ? $t('obligation') : $t('cost')"
                        :disabled="(isObligationCapped && !isObligationAmountEditable) || !!selectedTractGroup"
                        :hint="requirement(TractRequiredField.Cost).hint || null"
                        @val-changed="tractCostEntered"
                        :extraRules="['validTractCost']"/>
                      <v-text-field
                        v-else-if="type?.specialization === TractTypeSpecialization.Depletion.value"
                        :disabled="!streamlineDepletion"
                        :label="$t('cost')"
                        elementId="depletion-cost"
                        :value="tract?.type?.specialization === TractTypeSpecialization.Depletion.value ? formatMoney(originalTract?.cost) : formatMoney(0)"
                        color="black"
                        @focus="$emit('show-payment-form')"
                        ref="depletionCostField">
                        <template #append>
                          <Icon
                            v-if="streamlineDepletion"
                            icon="mdi-cash-multiple"
                            iconColor="black"
                            @icon-clicked="if (streamlineDepletion) $emit('show-payment-form')"
                            :tooltipText="$t('depletingTractCostSetViaCostPayments')"/>
                          <Icon v-else
                            icon="mdi-cash-multiple"
                            iconColor="black"
                            :tooltipText="$t('depletingTractCostSetViaCostPayments')"/>
                        </template>
                      </v-text-field>
                    </LoadableComponent>
                  </v-col>
                  <v-col cols="6" v-if="isStumpageTract">
                    <LoadableComponent :loading="loading">
                      <DatePicker
                        :dateLabel="$t('purchaseDate')"
                        :startDate="tract.purchaseDate"
                        elementId="tract-purchase-date"
                        clearable
                        :hint="requirement(TractRequiredField.PurchaseDate).hint"
                        :max="tract.harvestStartDate"
                        @date-picked="purchaseDatePicked"
                        @valid-date="(val) => { isValidPurchaseDate = val }"/>
                    </LoadableComponent>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" lg="4" v-if="isDeliveredTract">
        <v-card outlined class="fill-height">
          <v-card-title class="subtitle-1 pb-0">{{ $t('timberClassification') }}</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12">
                <LoadableComponent :loading="loading">
                  <v-autocomplete
                    clearable
                    :items="allTimberCategories"
                    itemText="name"
                    itemValue="timberCategoryId"
                    :label="$t('timberCategory')"
                    v-model="tract.timberCategoryId"
                    v-bind="requirementAttrs(TractRequiredField.TimberCategory)"
                    id="timber-category-autocomplete"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" class="mt-n3">
                <LoadableComponent :loading="loading">
                  <v-autocomplete
                    clearable
                    :items="allConversions"
                    itemText="name"
                    itemValue="conversionId"
                    :label="$t('conversion')"
                    v-model="tract.conversionId"
                    v-bind="requirementAttrs(TractRequiredField.Conversion)"
                    id="conversion-autocomplete"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" class="mt-n3">
                <LoadableComponent :loading="loading">
                  <v-text-field
                    v-if="hasLandowners"
                    readonly
                    :value="derivedLandownerTypeName || $t('notSpecified')"
                    :label="$t('landownerType')"
                    :hint="$t('landownerTypeDerivedFromLandowners')"
                    persistent-hint
                    id="landowner-type-field"/>
                  <v-autocomplete
                    v-else
                    clearable
                    :items="allLandownerTypes"
                    itemText="name"
                    itemValue="landownerTypeId"
                    :label="$t('landownerType')"
                    v-model="tract.landownerTypeId"
                    id="landowner-type-autocomplete"/>
                </LoadableComponent>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" :lg="isDeliveredTract ? 4 : 6">
        <v-card outlined class="fill-height">
          <v-card-title class="subtitle-1 pb-0">{{ $t('team') }}</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" v-if="showTeamSelection">
                <LoadableComponent :loading="loading">
                  <TeamAutocomplete
                    :propTeamId="tract.teamId"
                    @team-chosen="teamChosen"
                    :hint="requirement(TractRequiredField.Team).hint"
                    :rules="requirement(TractRequiredField.Team).rules"
                    :readonly="!!selectedTractGroup"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12">
                <LoadableComponent :loading="loading">
                  <UserAutocomplete
                    :label="$t('forester')"
                    :propUserId="tract.foresterUserId"
                    elementId="tract-forester"
                    @user-chosen="foresterChosen"
                    :hint="requirement(TractRequiredField.Forester).hint"
                    :rules="requirement(TractRequiredField.Forester).rules"
                    :fetchUsers="false"
                    :allUserIds="!!team ? tract.userIds ?? [] : undefined"
                    :readonly="!!selectedTractGroup"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" v-if="showTeamSelection">
                <LoadableComponent :loading="loading">
                  <UserAutocomplete
                    multiple
                    chips
                    smallChips
                    deletableChips
                    elementId="tract-users-autocomplete"
                    :label="$t('users')"
                    :fetchUsers="false"
                    :propTeam="team"
                    :propUserIds="propTract.userIds"
                    :includePreexistingUser="false"
                    :rules="!!team ? [rules.requiredSelect] : []"
                    @user-chosen="usersChosen"/>
                </LoadableComponent>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" :lg="isDeliveredTract ? 4 : 6">
        <v-card outlined class="fill-height">
          <v-card-title class="subtitle-1 pb-0">{{ $t('harvest') }}</v-card-title>
          <v-card-text>
            <v-row dense class="mt-3">
              <v-col cols="12">
                <LoadableComponent :loading="loading">
                  <v-text-field
                    :label="$t('availabilityPeriod')"
                    :value="availabilityDatesText"
                    :hint="availabilityRequirement.hint"
                    :persistent-hint="availabilityRequirement.hint !== ''"
                    readonly
                    outlined
                    dense>
                    <template #append>
                      <Icon
                        icon="mdi-pencil"
                        :small="false"
                        @icon-clicked="editAvailabilityPeriodClicked"/>
                    </template>
                  </v-text-field>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" :class="availabilityRequirement.hint ? 'mt-n2' : 'mt-n6'">
                <LoadableComponent :loading="loading">
                  <v-text-field
                    :label="$t('harvestPeriod')"
                    :value="harvestDatesText"
                    :hint="harvestRequirement.hint"
                    :persistent-hint="harvestRequirement.hint !== ''"
                    readonly
                    outlined
                    dense>
                    <template #append>
                      <Icon
                        icon="mdi-pencil"
                        :small="false"
                        @icon-clicked="editHarvestPeriodClicked"/>
                    </template>
                  </v-text-field>
                </LoadableComponent>
              </v-col>
              <v-col cols="12" :class="harvestRequirement.hint ? 'mt-n2' : 'mt-n6'">
                <LoadableComponent :loading="loading">
                  <v-checkbox
                    v-if="!isGrouped"
                    id="contract-requires-ext-ticket-1"
                    v-model="tract.requiresExt1"
                    color="primary"
                    :label="$t('requiresExt1')"
                    hide-details
                    class="mt-0"/>
                  <v-row v-else no-gutters align="center" class="mt-0">
                    <v-col cols="auto">
                      <v-icon :color="tract.requiresExt1 ? 'primary' : 'grey'">
                        {{ tract.requiresExt1 ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                      </v-icon>
                    </v-col>
                    <v-col class="ml-2">
                      {{ $t('requiresExt1') }}
                    </v-col>
                  </v-row>
                </LoadableComponent>
              </v-col>
              <v-col cols="12">
                <LoadableComponent :loading="loading">
                  <v-row no-gutters align="center">
                    <v-col cols="auto" v-if="!isGrouped">
                      <v-checkbox
                        id="contract-requires-ext-ticket-2"
                        v-model="tract.requiresExt2"
                        color="primary"
                        :label="$t('requiresExt2')"
                        hide-details
                        class="mt-0">
                        <template #append>
                          <Icon
                            iconColor="primary"
                            :disabled="!tract.requiresExt2"
                            icon="mdi-book-open-variant"
                            :small="false"
                            :tooltipText="$t('specifyTicketBooks')"
                            @icon-clicked="openTicketBookDialog"/>
                        </template>
                      </v-checkbox>
                    </v-col>
                    <template v-else>
                      <v-col cols="auto">
                        <v-icon :color="tract.requiresExt2 ? 'primary' : 'grey'">
                          {{ tract.requiresExt2 ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                        </v-icon>
                      </v-col>
                      <v-col cols="auto" class="ml-2">
                        {{ $t('requiresExt2') }}
                      </v-col>
                      <v-col cols="auto" class="ml-2">
                        <Icon
                          iconColor="primary"
                          :disabled="!tract.requiresExt2"
                          icon="mdi-book-open-variant"
                          :small="false"
                          :tooltipText="$t('specifyTicketBooks')"
                          @icon-clicked="openTicketBookDialog"/>
                      </v-col>
                    </template>
                    <v-col v-if="tract?.ticketBookIds?.length > 0" class="ml-4 text--secondary">
                      {{ ticketBookSubtitleString }}
                    </v-col>
                  </v-row>
                </LoadableComponent>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" v-if="!isGrouped">
        <v-card outlined>
          <v-card-title class="subtitle-1 pb-0">
            {{ $t('landowners') }}
            <span v-if="requirement(TractRequiredField.Landowners).hint" class="caption text--secondary ml-2">{{ requirement(TractRequiredField.Landowners).hint }}</span>
          </v-card-title>
          <v-card-text>
            <TractLandowners
              :propLandowners="landowners"
              :loading="loading"
              :disabled="!!selectedTractGroup"
              @ownership-changed="landowners = $event"/>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="4">
        <v-card outlined class="fill-height">
          <v-card-title class="subtitle-1 pb-0">{{ $t('loggers') }}</v-card-title>
          <v-card-text>
            <LoadableComponent :loading="loading">
              <LoggerAutocomplete
                :loggers="tract.loggers"
                userSetting="accountLoggerAutocomplete"
                @loggers-mutated="loggersChosen"
                elementId="tract-loggers"
                :hint="requirement(TractRequiredField.Loggers).hint"
                :rules="requirement(TractRequiredField.Loggers).rules"
                :fetchTagsAndAccounts="false"/>
            </LoadableComponent>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="4">
        <v-card outlined class="fill-height">
          <v-card-title class="subtitle-1 pb-0">{{ $t('certifications') }}</v-card-title>
          <v-card-text>
            <LoadableComponent :loading="loading">
              <TractCertificationBases
                elementId="tract-certifications"
                :certificationIds="tract.certificationIds"
                :accountIds="certificationBasisAccountIds"
                :propAssignments="tract.certificationAssignments"
                :hint="requirement(TractRequiredField.Certifications).hint"
                :rules="requirement(TractRequiredField.Certifications).rules"
                @certifications="certificationChosen"
                @assignments-changed="tract.certificationAssignments = $event"/>
            </LoadableComponent>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" lg="4">
        <v-card outlined>
          <v-card-title class="subtitle-1 pb-0">{{ $t('quality') }}</v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="4">
                <LoadableComponent :loading="loading">
                  <v-text-field
                    id="rpi"
                    :label="$t('rpi')"
                    v-model="tract.rpi"
                    :rules="[rules.numberOrBlank, rules.twoDecimalPlacesOrFewer, rules.validRPI]"
                    maxLength="5"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="4">
                <LoadableComponent :loading="loading">
                  <v-text-field
                    id="dbh"
                    :label="$t('dbh')"
                    v-model="tract.dbh"
                    :rules="[rules.numberOrBlank, rules.positiveIntegerOrBlank, rules.validDBH]"
                    maxLength="3"/>
                </LoadableComponent>
              </v-col>
              <v-col cols="4">
                <LoadableComponent :loading="loading">
                  <v-text-field
                    id="age"
                    :label="$t('age')"
                    v-model="tract.age"
                    :rules="[rules.numberOrBlank, rules.positiveIntegerOrBlank, rules.validAge]"
                    maxLength="3"/>
                </LoadableComponent>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialogs -->
    <v-dialog v-model="ticketBookDialog" max-width="800px" v-if="ticketBookDialog">
      <TicketBookSelector
        v-if="ticketBookDialog"
        :accountIds="ticketBookAccountIds"
        :propTicketBookIds="tract.ticketBookIds"
        :propTicketBooksAccountIds="tract.ticketBookAccountIds"
        :tractId="propTract.tractId"
        @close="ticketBookDialog = false"
        @ticket-books-selected="ticketBooksSelected"/>
    </v-dialog>

    <Dialog :stateId="dialogId" @close="closeDialog" @dialog-closing="closeDialog" @click:outside="closeDialog">
      <HarvestPeriodForm
        v-if="editingHarvestPeriod"
        :tract="tract"
        :originalTract="propOriginalTract"
        :startHint="requirement(TractRequiredField.HarvestStartDate).hint"
        :endHint="requirement(TractRequiredField.HarvestEndDate).hint"
        @confirm="harvestPeriodConfirmed"/>
      <AvailabilityPeriodForm
        v-if="editingAvailabilityPeriod"
        :tract="tract"
        :startHint="requirement(TractRequiredField.AvailabilityStartDate).hint"
        :endHint="requirement(TractRequiredField.AvailabilityEndDate).hint"
        @confirm="availabilityPeriodConfirmed"/>
    </Dialog>
  </v-container>
</template>

<script>
import fieldRules from '@/utils/rules.js'
import { TractTypeCategory, TractTypeSpecialization, TractPayableSpecialization, TractStatus, TractRequiredField } from '@/utils/Enumerations.js'
import { requirementAnnotation } from '@/utils/TractRequirements.js'
import { formatMoney } from '@/utils/NumericMutations'
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import { uniqueDialogId } from '@/utils/componentHelpers'

export default {
  name: 'GeneralInformation',

  props: {
    propTract: Object,
    propLandowners: Array,
    propOriginalTract: Object,
    tractTypeFilter: {
      type: Function,
      default: null
    },
    autofocusName: {
      type: Boolean,
      default: false
    }
  },

  components: {
    TractCertificationBases: () => import('@/components/tract/tract-form/TractCertificationBases.vue'),
    TractTypeSelector: () => import('@/components/tract/TractTypeSelector.vue'),
    TractGroupAutocomplete: () => import('@/components/autocomplete/TractGroupAutocomplete.vue'),
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    SettingAutocomplete: () => import('@/components/autocomplete/ContractSettingAutocomplete.vue'),
    UserAutocomplete: () => import('@/components/autocomplete/UserAutocomplete.vue'),
    LoggerAutocomplete: () => import('@/components/autocomplete/LoggerAutocomplete.vue'),
    EntitySelector: () => import('@/components/accounting/EntitySelector.vue'),
    MoneyTextField: () => import('@/components/helper/MoneyTextField.vue'),
    DatePicker: () => import('@/components/helper/DatePicker.vue'),
    TractLandowners: () => import('@/components/tract/tract-form/TractLandowners.vue'),
    TeamAutocomplete: () => import('@/components/autocomplete/TeamAutocomplete.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    TicketBookSelector: () => import('@/components/tract/tract-form/TicketBookSelector.vue'),
    HarvestPeriodForm: () => import('@/components/tract/tract-form/HarvestPeriodForm.vue'),
    AvailabilityPeriodForm: () => import('@/components/tract/tract-form/AvailabilityPeriodForm.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  data: () => ({
    TractTypeSpecialization,
    TractRequiredField,
    TractStatus,
    rules: fieldRules.rules,
    entities: [],
    isValidStartDate: true,
    renderDates: true,
    isValidEndDate: true,
    isValidPurchaseDate: true,
    applyDateChangesToContracts: false,
    originalHarvestStart: null,
    originalHarvestEnd: null,
    originalTract: null,
    selectedTractGroup: null,
    tract: {
      name: '',
      code: '',
      tractGroupId: null,
      type: {
        tractTypeId: null
      },
      cost: 0,
      haulerAccountId: null,
      supplierAccountId: null,
      contractAccountId: null,
      contractSettingId: null,
      contractSetting: null,
      loggerAccountId: null,
      loggers: [],
      loggerAccountIds: [],
      certificationIds: [],
      certificationAssignments: [],
      harvestStartDate: new Date().toISOString(),
      harvestEndDate: new Date().toISOString(),
      availabilityStartDate: null,
      availabilityEndDate: null,
      purchaseDate: new Date().toISOString(),
      requiresExt1: false,
      requiresExt2: false,
      consultingForesterAccountId: null,
      foresterUserId: null,
      rpi: null,
      dbh: null,
      age: null,
      userIds: [],
      teamId: undefined,
      harvestTypeId: null,
      loggingMethodId: null,
      timberCategoryId: null,
      conversionId: null,
      landownerTypeId: null,
      ticketBookIds: [],
      ticketBookAccountIds: [],
      businessEntity: {
        accountId: null,
        name: '',
        businessEntityId: null
      }
    },
    landowners: [],
    loading: true,
    team: undefined,
    payables: [],
    ticketBookDialog: false,
    initialTicketBookIds: [],
    initialTicketBookAccountIds: [],
    dialogId: uniqueDialogId('tract-general-info'),
    editingHarvestPeriod: false,
    editingAvailabilityPeriod: false
  }),

  computed: {
    ...mapGetters('team', ['allTeams']),
    ...mapGetters('harvest-type', ['allHarvestTypes']),
    ...mapGetters('logging-method', ['allLoggingMethods']),
    ...mapGetters('tract', ['allTimberCategories', 'allConversions', 'allLandownerTypes']),
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('tract', ['allTractGroups']),
    ...mapGetters('tract-type', ['allTractTypes']),

    selectedTractType () {
      return this.allTractTypes.find(tt => tt.tractTypeId === this.tract.type?.tractTypeId)
    },

    harvestRequirement () {
      return this.requirement([TractRequiredField.HarvestStartDate, TractRequiredField.HarvestEndDate])
    },

    availabilityRequirement () {
      return this.requirement([TractRequiredField.AvailabilityStartDate, TractRequiredField.AvailabilityEndDate])
    },

    inheritedProperties () {
      if (!this.selectedTractGroup) return []
      const tg = this.selectedTractGroup
      return [
        { key: 'tractType', value: tg.tractTypeName },
        { key: 'forester', value: tg.foresterUserName },
        { key: 'consultingForester', value: tg.consultingForesterAccountName },
        { key: 'team', value: tg.teamName },
        { key: 'businessEntity', value: tg.businessEntityName },
        { key: 'landowners', value: tg.landownerCount > 0 ? `${tg.landownerCount} landowner(s)` : null }
      ]
    },

    derivedLandownerTypeName () {
      const ranked = this.landowners
        .filter(l => l?.accountId)
        .sort((a, b) => b.ownership - a.ownership)
      if (ranked.length === 0 || (ranked.length > 1 && ranked[1].ownership === ranked[0].ownership)) return undefined
      const account = this.allAccounts.find(a => a.accountId === ranked[0].accountId)
      return this.allLandownerTypes.find(lt => lt.landownerTypeId === account?.landownerTypeId)?.name
    },

    hasLandowners () {
      return this.landowners.some(l => l?.accountId)
    },

    isGrouped () {
      return this.tract.tractGroupId != null
    },

    isStumpageTract () {
      return this.tract.type?.category === TractTypeCategory.Stumpage.value
    },

    isDeliveredTract () {
      return this.tract.type?.category === TractTypeCategory.Delivered.value
    },

    type () {
      return this.tract.type
    },

    teamUserIds () {
      return this.team?.users?.map(u => u.applicationUserId)
    },

    showTeamSelection () {
      return this.allTeams?.filter(t => t.isActive).length > 0 || this.propTract.teamId || this.propTract.users?.length > 0
    },

    editing () {
      return this.propTract?.tractId !== undefined && this.propTract?.tractId !== null
    },

    streamlineDepletion () {
      return this.editing &&
        this.tract?.cost === 0 &&
        this.payables.length === 0 &&
        this.originalTract?.type?.specialization === TractTypeSpecialization.Depletion.value // Depletion behavior must be saved in order to issue tract cost payable
    },

    isObligationUncapped () {
      return this.tract.type?.specialization === TractTypeSpecialization.ObligationUncapped.value
    },

    isObligationCapped () {
      return this.tract.type?.specialization === TractTypeSpecialization.ObligationCapped.value
    },

    isObligationAmountEditable () {
      if (!this.isObligationCapped) return true

      const nonEditableStatuses = [
        TractStatus.Active.value,
        TractStatus.Closed.value,
        TractStatus.Archived.value
      ]

      return !nonEditableStatuses.includes(this.propTract.status)
    },

    certificationBasisAccountIds () {
      return [
        ...this.landowners.map(l => l?.accountId),
        ...this.tract.loggerAccountIds,
        this.tract.loggerAccountId,
        this.tract.supplierAccountId,
        this.tract.consultingForesterAccountId
      ].filter(a => a != null)
    },

    ticketBookAccountIds () {
      const landownerAccountIds = this.landowners.map(l => l?.accountId)
      const loggerAccountIds = this.tract.loggerAccountIds.map(l => l)
      return [
        ...landownerAccountIds,
        ...loggerAccountIds,
        this.tract.supplierAccountId,
        this.tract.loggerAccountId,
        this.businessEntityAccountId ?? this.tract.businessEntity?.accountId
      ].filter(a => a != null)
    },

    tractTicketBookAccounts () {
      return (this.tract.ticketBookAccountIds ?? []).map(accountId => ({ accountId, tooltipText: this.$t('tractTicketBookAccount') }))
    },

    ticketBookSubtitleString () {
      if ((this.tract.ticketBookIds?.length ?? 0) === 0) {
        return ''
      }

      const accountNameString = this.tract.ticketBookAccountIds.length > 1 ? this.$t('multipleAccounts') : this.allAccounts.find(a => a.accountId === this.tract.ticketBookAccountIds[0])?.name || ''
      const ticketBookString = this.tract.ticketBookIds.length === 1
        ? this.$t('ticketBook')
        : this.$t('ticketBooks')

      return `${accountNameString} - ${this.tract.ticketBookIds.length} ${ticketBookString}`
    },

    harvestDatesText () {
      const { harvestStartDate, harvestEndDate } = this.tract
      if (harvestStartDate || harvestEndDate) {
        const start = harvestStartDate ? moment(harvestStartDate).format('MM/DD/YYYY') : this.$t('notSpecified')
        const end = harvestEndDate ? moment(harvestEndDate).format('MM/DD/YYYY') : this.$t('notSpecified')
        return `${start} \u2014 ${end}`
      }
      return this.$t('notSpecified')
    },

    availabilityDatesText () {
      const { availabilityStartDate, availabilityEndDate } = this.tract
      if (availabilityStartDate || availabilityEndDate) {
        const start = availabilityStartDate ? moment(availabilityStartDate).format('MM/DD/YYYY') : this.$t('notSpecified')
        const end = availabilityEndDate ? moment(availabilityEndDate).format('MM/DD/YYYY') : this.$t('notSpecified')
        return `${start} \u2014 ${end}`
      }
      return this.$t('notSpecified')
    },

    businessEntityAccountId () {
      return this.entities.find(e => e.businessEntityId === this.tract.businessEntityId)?.accountId
    }
  },

  watch: {
    tract: {
      handler (val) {
        this.$emit('tract-changed', val)
      },
      deep: true
    },

    'propTract.tractId': {
      async handler (val) {
        if (val !== null && val !== undefined) {
          this.payables = await this.fetchTractPayables(this.propTract.tractId)
        }
      },
      immediate: true
    },

    type (val, oldVal) {
      if (oldVal.category === TractTypeCategory.Delivered.value && val.category !== TractTypeCategory.Delivered.value) {
        this.tract.timberCategoryId = null
        this.tract.conversionId = null
        this.tract.landownerTypeId = null
      }

      if (val.specialization === TractTypeSpecialization.Depletion.value && oldVal.specialization !== TractTypeSpecialization.Depletion.value) {
        if (this.originalTract?.type?.specialization === TractTypeSpecialization.Depletion.value) {
          this.tract.cost = this.originalTract.cost
        } else {
          this.tract.cost = 0
          this.originalTract.cost = 0
        }
      }

      if (val.specialization === TractTypeSpecialization.ObligationUncapped.value) {
        this.tract.cost = this.originalTract?.cost ?? 0
      }

      if (this.propTract.tractId) {
        return
      }

      const selectedDeliveredType = oldVal.tractTypeId === null && val.category === TractTypeCategory.Delivered.value
      const switchedFromStumpage = val.category === TractTypeCategory.Delivered.value && (!this.tract.harvestStartDate && !this.tract.harvestEndDate)
      if (selectedDeliveredType || switchedFromStumpage) {
        if (!this.tract.harvestStartDate && !this.tract.harvestEndDate) {
          this.tract.harvestStartDate = moment().startOf('day').format()
          this.tract.harvestEndDate = moment().add(90, 'day').startOf('day').format()
        }
        if (!this.tract.availabilityStartDate && !this.tract.availabilityEndDate) {
          this.tract.availabilityStartDate = this.tract.harvestStartDate
          this.tract.availabilityEndDate = this.tract.harvestEndDate
        }
        this.renderDates = false
        this.$nextTick(_ => {
          this.renderDates = true
        })
      }
    },

    applyDateChangesToContracts (shouldApplyDates) {
      this.$emit('apply-to-contracts-toggled', shouldApplyDates)
    },

    landowners: {
      handler (val) {
        this.$emit('ownership-changed', val)
      },
      deep: true
    },

    hasLandowners (val) {
      if (val) this.tract.landownerTypeId = null
    },

    propLandowners: {
      handler () {
        this.landowners = this.propLandowners
      },
      deep: true
    },

    team (team) {
      this.tract.teamId = team?.teamId
    },

    propOriginalTract: {
      handler (val) {
        this.originalTract = JSON.parse(JSON.stringify(val))
      },
      deep: true,
      immediate: true
    },

    'tract.requiresExt2' (val) {
      if (!val) {
        this.tract.ticketBookIds = []
        this.tract.ticketBookAccountIds = []
      } else if (this.initialTicketBookIds.length > 0) {
        this.tract.ticketBookIds = this.initialTicketBookIds
        this.tract.ticketBookAccountIds = this.initialTicketBookAccountIds
      }
    }
  },

  async created () {
    this.setInitialValues(this.propTract, this.propLandowners)
    const [entities] = await Promise.all([
      this.fetchAllBusinessEntities(),
      this.fetchTeams(),
      this.fetchAccounts(),
      this.fetchAccountTags(),
      this.getAllUsers(),
      this.fetchCertifications(),
      this.fetchSettings(),
      this.fetchTractTypes(),
      this.fetchHarvestTypes(),
      this.fetchLoggingMethods(),
      this.fetchTimberCategories(),
      this.fetchConversions(),
      this.fetchLandownerTypes(),
      this.fetchTractGroups()
    ])
    if (this.tract.teamId) this.team = this.allTeams.find(t => t.teamId === this.tract.teamId)
    this.entities = entities
    this.tract.businessEntity = entities.find(e => e.businessEntityId === this.propTract?.businessEntityId)
    if (this.tract.tractGroupId) {
      this.selectedTractGroup = this.allTractGroups.find(tg => tg.tractGroupId === this.tract.tractGroupId)
    }
    this.loading = false
    if (this.autofocusName) this.$nextTick(() => this.$refs.firstField?.focus())
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('account', ['fetchAccounts']),

    requirement (fields) {
      return requirementAnnotation(this.selectedTractType, fields)
    },

    requirementAttrs (fields) {
      const { hint, rules } = this.requirement(fields)
      return { hint, 'persistent-hint': hint !== '', rules }
    },

    ...mapActions('tags', ['fetchAccountTags']),
    ...mapActions('user', ['fetchAllBusinessEntities', 'getAllUsers']),
    ...mapActions('certification', ['fetchCertifications']),
    ...mapActions('setting', ['fetchSettings']),
    ...mapActions('tract-type', ['fetchTractTypes']),
    ...mapActions('team', ['fetchTeams']),
    ...mapActions('tract', ['fetchTractPayables', 'fetchTractGroups', 'fetchTractGroup']),
    ...mapActions('harvest-type', ['fetchHarvestTypes']),
    ...mapActions('logging-method', ['fetchLoggingMethods']),
    ...mapActions('tract', ['fetchTimberCategories', 'fetchConversions', 'fetchLandownerTypes']),
    formatMoney,

    setInitialValues (propTract, propLandowners) {
      this.tract.name = propTract.name
      this.tract.code = propTract.code
      this.tract.tractGroupId = propTract.tractGroupId
      this.tract.type = propTract.type
      this.tract.foresterUserId = propTract.foresterUser?.applicationUserId
      this.tract.harvestStartDate = propTract.harvestStartDate
      this.tract.harvestEndDate = propTract.harvestEndDate
      this.tract.availabilityStartDate = propTract.availabilityStartDate
      this.tract.availabilityEndDate = propTract.availabilityEndDate
      this.tract.purchaseDate = propTract.purchaseDate
      this.tract.cost = propTract.cost
      this.tract.businessEntity = {
        name: propTract.businessEntity,
        businessEntityId: propTract.businessEntityId
      }
      this.tract.loggerAccountId = propTract.loggerAccountId
      this.tract.haulerAccountId = propTract.haulerAccountId
      this.tract.supplierAccountId = propTract.supplierAccountId
      this.tract.contractAccountId = propTract.contractAccountId
      this.tract.contractSettingId = propTract.contractSettingId
      this.tract.contractSetting = propTract.contractSetting
      this.tract.requiresExt1 = propTract.requiresExt1 ?? false
      this.tract.requiresExt2 = propTract.requiresExt2 ?? false
      this.tract.certificationIds = propTract.certificationIds
      this.tract.certificationAssignments = propTract.certificationAssignments ?? []
      this.tract.loggers = propTract.loggers ?? []
      this.originalHarvestStart = this.propTract.harvestStartDate
      this.originalHarvestEnd = this.propTract.harvestEndDate
      this.tract.consultingForesterAccountId = propTract.consultingForesterAccountId
      this.tract.rpi = propTract.rpi || undefined
      this.tract.dbh = propTract.dbh || undefined
      this.tract.age = propTract.age || undefined
      this.landowners = propLandowners
      this.tract.userIds = propTract.userIds
      this.tract.teamId = propTract.teamId
      this.tract.harvestTypeId = propTract.harvestTypeId
      this.tract.loggingMethodId = propTract.loggingMethodId
      this.tract.timberCategoryId = propTract.timberCategoryId
      this.tract.conversionId = propTract.conversionId
      this.tract.landownerTypeId = propTract.landownerTypeId
      this.tract.ticketBookIds = propTract.ticketBooks?.map(tb => tb.ticketBookId) || []
      this.tract.ticketBookAccountIds = propTract.ticketBooks?.length > 0
        ? Array.from(new Set(propTract.ticketBooks.map(tb => tb.accountId)))
        : null

      this.initialTicketBookIds = this.tract.ticketBookIds
      this.initialTicketBookAccountIds = this.tract.ticketBookAccountIds
    },

    applyStampedValues (tract) {
      this.tract.harvestStartDate = tract.harvestStartDate
      this.tract.harvestEndDate = tract.harvestEndDate
      this.tract.supplierAccountId = tract.supplierAccountId
      this.tract.contractAccountId = tract.contractAccountId
    },

    tractCostEntered (cost) {
      this.tract.cost = cost
    },

    tractTypeChosen (tractType) {
      if (tractType.category === TractTypeCategory.Delivered.value) {
        this.tract.businessEntity = null
      }
      this.tract.type = tractType
    },

    async tractGroupChosen (tractGroup) {
      this.selectedTractGroup = tractGroup
      this.tract.tractGroupId = tractGroup?.tractGroupId ?? null
      if (tractGroup) {
        this.tract.type = { tractTypeId: tractGroup.tractTypeId }
        this.tract.foresterUserId = tractGroup.foresterUserId
        this.tract.consultingForesterAccountId = tractGroup.consultingForesterAccountId
        this.tract.teamId = tractGroup.teamId
        this.tract.businessEntityId = tractGroup.businessEntityId
        this.tract.businessEntity = this.entities.find(e => e.businessEntityId === tractGroup.businessEntityId) || null
        this.tract.requiresExt1 = tractGroup.requiresExt1
        this.tract.requiresExt2 = tractGroup.requiresExt2
        const group = await this.fetchTractGroup(tractGroup.tractGroupId)
        if (this.tract.tractGroupId !== tractGroup.tractGroupId) return
        this.landowners = group.landowners ?? []
      } else {
        this.landowners = []
      }
    },

    certificationChosen (certifications) {
      this.tract.certificationIds = certifications
    },

    entitySelected (entity) {
      this.tract.businessEntity = entity
    },

    loggersChosen (loggers) {
      this.tract.loggerAccountIds = loggers.map(l => l.accountId)
    },

    defaultLoggerChosen (defaultLogger) {
      this.tract.loggerAccountId = defaultLogger?.accountId
    },

    haulerChosen (hauler) {
      this.tract.haulerAccountId = hauler?.accountId
    },

    supplierChosen (supplier) {
      this.tract.supplierAccountId = supplier?.accountId
    },

    consultingForesterChosen (consultingForester) {
      this.tract.consultingForesterAccountId = consultingForester?.accountId
    },

    contractAccountChosen (contractAccount) {
      this.tract.contractAccountId = contractAccount?.accountId
    },

    contractSettingChosen (setting) {
      this.tract.contractSettingId = setting?.settingId
    },

    foresterChosen (forester) {
      this.tract.foresterUserId = forester?.applicationUserId ?? null
    },

    startDatePicked (startDate) {
      this.tract.harvestStartDate = startDate
    },

    endDatePicked (endDate) {
      this.tract.harvestEndDate = endDate
    },

    purchaseDatePicked (purchaseDate) {
      this.tract.purchaseDate = purchaseDate
    },

    teamChosen (team) {
      this.team = team
      if (team?.users) this.usersChosen(team.users)
    },

    ticketBookSelected (ticketBook) {
      this.tract.ticketBookId = ticketBook?.ticketBookId
    },

    usersChosen (users) {
      if (users) { this.tract.userIds = users.filter(u => u !== undefined && u !== null).map(u => u.applicationUserId) } else this.tract.userIds = []
    },

    userIdsChosen (userIds) {
      this.tract.userIds = userIds
    },

    openTicketBookDialog () {
      if (this.ticketBookAccountIds.length === 0) {
        this.setSnackError(this.$t('noAccountsSpecifiedForTract'))
        return
      }
      this.ticketBookDialog = true
    },

    ticketBooksSelected (ticketBooksPayload) {
      this.tract.ticketBookIds = ticketBooksPayload.selectedTicketBookIds
      this.tract.ticketBookAccountIds = ticketBooksPayload.accountIds
      this.initialTicketBookIds = ticketBooksPayload.selectedTicketBookIds
      this.initialTicketBookAccountIds = ticketBooksPayload.accountIds
      this.ticketBookDialog = false
    },

    openDialog () {
      this.openOrUpdateDialog({ id: this.dialogId, width: '600px' })
    },

    closeDialog () {
      this.closeDialogsAtOrAbove(this.dialogId)
      this.editingHarvestPeriod = false
      this.editingAvailabilityPeriod = false
    },

    editHarvestPeriodClicked () {
      this.openDialog()
      this.editingHarvestPeriod = true
    },

    harvestPeriodConfirmed (period) {
      this.tract.harvestStartDate = period.harvestStart
      this.tract.harvestEndDate = period.harvestEnd
      this.tract.availabilityEndDate = period.availabilityEnd
      this.applyDateChangesToContracts = period.applyToContracts
      this.closeDialog()
    },

    editAvailabilityPeriodClicked () {
      this.openDialog()
      this.editingAvailabilityPeriod = true
    },

    availabilityPeriodConfirmed (period) {
      this.tract.availabilityStartDate = period.availabilityStart
      this.tract.availabilityEndDate = period.availabilityEnd
      this.closeDialog()
    },

    async refreshPayables () {
      if (this.propTract?.tractId) {
        this.payables = await this.fetchTractPayables(this.propTract.tractId)
        if (this.payables.some(p => p.specialization === TractPayableSpecialization.Cost.value)) {
          this.tract.cost = this.payables.reduce((total, p) => p.specialization === TractPayableSpecialization.Cost.value ? total + p.amount : total, 0)
        }
      }
    }
  }
}
</script>

<style scoped>
.inherited-properties-card {
  background-color: #f5f5f5;
}
</style>
